import Axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import router from '@/router';
import SecureLS from 'secure-ls';
import { IUssdCallbackBody, IUssdCallbackUpdate } from '@/types/types';

let ls = new SecureLS({
  isCompression: false,
});

// Talks to messaging-ussd-mtn-aggregator-nodejs, which guards /admin/ussd with
// the shared JWT. Token is read per request so a re-login takes effect without
// a full page reload.
const UssdAdminRequest = () => {
  const baseHeader: AxiosInstance = Axios.create({
    baseURL: process.env.VUE_APP_USSD_AGGREGATOR_BASE_URL,
    timeout: 60000,
  });

  baseHeader.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      config.headers.common['Accept'] = 'application/json';
      config.headers.common['Content-Type'] = 'application/json';
      try {
        const session = JSON.parse(ls.get('cloud.access'))?.auth?.user?.token;
        if (session) {
          config.headers.common['authorization'] = `Bearer ${session}`;
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
      return config;
    },
    error => Promise.reject(error),
  );

  baseHeader.interceptors.response.use(
    (response: AxiosResponse) => Promise.resolve(response),
    error => {
      // Background polls opt out: signing the operator out mid-form would throw
      // away everything typed, and the next real action reports the expiry anyway.
      if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
        localStorage.clear();
        router.replace('/signin').then();
      }
      return Promise.reject(error);
    },
  );
  return baseHeader;
};

export class UssdAdminService {
  static listCallbacks(query?: string) {
    return UssdAdminRequest().get(`/admin/ussd/callbacks${query || ''}`);
  }

  // Fires while the operator is still typing, so a slow answer is dropped early.
  static checkExtensionAvailability(extension: string) {
    return UssdAdminRequest().get(`/admin/ussd/callbacks/availability`, {
      params: { extension },
      timeout: 10000,
      skipAuthRedirect: true,
    } as AxiosRequestConfig);
  }

  static createCallback(body: IUssdCallbackBody) {
    return UssdAdminRequest().post(`/admin/ussd/callbacks`, body);
  }

  static updateCallback(id: string, body: IUssdCallbackUpdate) {
    return UssdAdminRequest().patch(`/admin/ussd/callbacks/${id}`, body);
  }

  static updateCallbackStatus(id: string, status: string) {
    return UssdAdminRequest().patch(`/admin/ussd/callbacks/${id}/status`, { status });
  }

  static listHits(query?: string) {
    return UssdAdminRequest().get(`/admin/ussd/hits${query || ''}`);
  }

  static hitStats(query?: string) {
    return UssdAdminRequest().get(`/admin/ussd/hits/stats${query || ''}`);
  }

  static exportHits(query?: string) {
    return UssdAdminRequest().get(`/admin/ussd/hits/export${query || ''}`, { responseType: 'blob' });
  }

  static listSessions(query?: string) {
    return UssdAdminRequest().get(`/admin/ussd/sessions${query || ''}`);
  }

  static releaseSession(sessionId: string) {
    return UssdAdminRequest().delete(`/admin/ussd/sessions/${sessionId}`);
  }
}
